/**
 * Trial Banner Component
 *
 * Shows a warning banner when the user's stamp is about to expire (< 3 days)
 * or has expired. Fetches live stamp status from Bee node instead of using
 * stale account.stampExpiresAt snapshot.
 *
 * Only shown when user has a personal stamp (account.stampId).
 * Users without a personal stamp don't see this banner.
 */

import { useState, useEffect } from 'react'
import { getStampInfo } from '../services/swarm/stamps'
import type { Account } from '../types'

export interface TrialBannerProps {
  /** Current account with stamp info */
  account: Account | null
  /** Callback to open settings panel */
  onOpenSettings: () => void
}

interface TrialState {
  show: boolean
  type: 'warning' | 'expired'
  daysRemaining: number
}

export function TrialBanner({ account, onOpenSettings }: TrialBannerProps) {
  const [trialState, setTrialState] = useState<TrialState>({ show: false, type: 'warning', daysRemaining: 0 })
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!account?.stampId) {
      // No personal stamp — don't show banner
      setTrialState({ show: false, type: 'warning', daysRemaining: 0 })
      return
    }

    let cancelled = false

    // Fetch live stamp status from Bee node
    getStampInfo(account.stampId).then(info => {
      if (cancelled) return

      if (!info) {
        // Stamp not found — treat as expired
        setTrialState({ show: true, type: 'expired', daysRemaining: 0 })
        return
      }

      const msRemaining = info.expiresAt - Date.now()
      const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24))

      if (!info.usable || msRemaining <= 0) {
        setTrialState({ show: true, type: 'expired', daysRemaining: 0 })
      } else if (daysRemaining <= 3) {
        setTrialState({ show: true, type: 'warning', daysRemaining })
      } else {
        setTrialState({ show: false, type: 'warning', daysRemaining })
      }
    }).catch(() => {
      if (cancelled) return
      // Network error — fall back to stored value if available
      if (account.stampExpiresAt) {
        const msRemaining = account.stampExpiresAt - Date.now()
        if (msRemaining <= 0) {
          setTrialState({ show: true, type: 'expired', daysRemaining: 0 })
        } else {
          setTrialState({ show: false, type: 'warning', daysRemaining: Math.ceil(msRemaining / (1000 * 60 * 60 * 24)) })
        }
      }
    })

    return () => { cancelled = true }
  }, [account?.stampId])

  // Reset dismissed state when account changes
  useEffect(() => {
    setDismissed(false)
  }, [account?.subdomain])

  if (!trialState.show || dismissed) {
    return null
  }

  const isExpired = trialState.type === 'expired'

  return (
    <div className={`trial-banner ${isExpired ? 'trial-banner-expired' : 'trial-banner-warning'}`}>
      <div className="trial-banner-content">
        <span className="trial-banner-icon">!</span>
        <span className="trial-banner-message">
          {isExpired ? (
            <>Your stamp has expired.</>
          ) : (
            <>Your stamp expires in {trialState.daysRemaining} day{trialState.daysRemaining !== 1 ? 's' : ''}.</>
          )}
        </span>
        <button className="trial-banner-link" onClick={onOpenSettings}>
          {isExpired ? 'Top up or replace stamp in Settings' : 'Top up stamp in Settings'}
        </button>
      </div>
      <button
        className="trial-banner-close"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  )
}

export default TrialBanner
